import {
    withHistoryUpdate,
    applyActionRecordsToDoc,
    applyMutativePatches,
    revertActionRecords as revertActionRecordsFromDoc,
} from "@buildwithflux/core";

import {buildDocumentStateUpdate, performDocumentMutation} from "../documentMutations";
import {notifyServicesOfAction} from "../serviceNotifier";
import type {DocumentStoreState} from "../types";

import type {SliceDependencies} from "./types";

type UndoRedoSliceState = Pick<
    DocumentStoreState,
    "undoDocument" | "redoDocument" | "applyActionRecords" | "revertActionRecords" | "revertDocumentVersion"
>;

export function createUndoRedoSlice(deps: SliceDependencies): UndoRedoSliceState {
    const {primitives, set, get, currentUserService, services, actionEventBus} = deps;

    return {
        undoDocument: (patches) => {
            if (!patches) {
                return;
            }
            performDocumentMutation(
                primitives,
                (draft) => {
                    applyMutativePatches(draft, patches);
                },
                {
                    actionType: "undoDocument",
                    canUndo: true,
                    shouldGenerateActionRecord: true,
                    updatesDocumentTimestamp: true,
                    additionalPayload: {
                        hasInheritedRules: true,
                        hasInheritedCalculations: true,
                        requiresDescendantRebake: true,
                        requiresNetDescendantRebake: true,
                    },
                },
            );
        },

        redoDocument: (patches) => {
            if (!patches) {
                return;
            }
            performDocumentMutation(
                primitives,
                (draft) => {
                    applyMutativePatches(draft, patches);
                },
                {
                    actionType: "redoDocument",
                    canUndo: true,
                    shouldGenerateActionRecord: true,
                    updatesDocumentTimestamp: true,
                    additionalPayload: {
                        hasInheritedRules: true,
                        hasInheritedCalculations: true,
                        requiresDescendantRebake: true,
                        requiresNetDescendantRebake: true,
                    },
                },
            );
        },

        applyActionRecords: (actionRecords) => {
            if (!actionRecords) {
                return;
            }
            performDocumentMutation(
                primitives,
                (draft) => {
                    applyActionRecordsToDoc(draft, actionRecords);
                },
                {
                    actionType: "applyActionRecords",
                    canUndo: false,
                    shouldGenerateActionRecord: false,
                    updatesDocumentTimestamp: false,
                    additionalPayload: {
                        hasInheritedRules: true,
                        hasInheritedCalculations: true,
                        requiresDescendantRebake: true,
                        requiresNetDescendantRebake: true,
                    },
                },
            );
        },

        revertActionRecords: (actionRecords) => {
            if (!actionRecords) {
                return;
            }
            performDocumentMutation(
                primitives,
                (draft) => {
                    revertActionRecordsFromDoc(draft, actionRecords);
                },
                {
                    actionType: "revertActionRecords",
                    canUndo: false,
                    shouldGenerateActionRecord: false,
                    updatesDocumentTimestamp: false,
                    additionalPayload: {
                        hasInheritedRules: true,
                        hasInheritedCalculations: true,
                        requiresDescendantRebake: true,
                        requiresNetDescendantRebake: true,
                    },
                },
            );
        },

        revertDocumentVersion: (actionRecords) => {
            if (!actionRecords) {
                return;
            }
            const document = get().document;
            if (!document) {
                return;
            }
            const newDocument = withHistoryUpdate(document, (draft) =>
                revertActionRecordsFromDoc(draft, actionRecords),
            );
            if (newDocument !== document) {
                set(buildDocumentStateUpdate(newDocument), false, "revertDocumentVersion");
            }
            notifyServicesOfAction(
                {
                    type: "revertDocumentVersion",
                    currentUser: currentUserService?.getCurrentUser(),
                    payload: {
                        canUndo: true,
                        shouldGenerateActionRecord: true,
                        updatesDocumentTimestamp: true,
                        hasInheritedRules: true,
                        hasInheritedCalculations: true,
                        requiresDescendantRebake: true,
                        requiresNetDescendantRebake: true,
                    },
                },
                services,
                actionEventBus,
            );
        },
    };
}
